import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { toast as heroToast } from '@heroui/react';

type ToastTone = 'success' | 'error' | 'info' | 'warning';

interface ToastContextValue {
  show(message: string, tone?: ToastTone): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_MAP: Record<ToastTone, 'success' | 'danger' | 'accent' | 'warning'> = {
  success: 'success',
  error: 'danger',
  info: 'accent',
  warning: 'warning',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const show = useCallback((message: string, tone: ToastTone = 'info') => {
    const variant = TONE_MAP[tone];
    if (variant === 'danger') {
      heroToast.danger(message, { timeout: 4500 });
    } else if (variant === 'success') {
      heroToast.success(message, { timeout: 4500 });
    } else if (variant === 'warning') {
      heroToast.warning(message, { timeout: 4500 });
    } else {
      heroToast(message, { variant: 'accent', timeout: 4500 });
    }
  }, []);

  const value = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast debe usarse dentro de ToastProvider.');
  return value;
}
